import { useState, FormEvent } from 'react';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSection() {
  const { ref, isVisible } = useIntersectionObserver(0.05);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [contactMethod, setContactMethod] = useState<string>('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    // 訪問の場合は住所必須チェック
    if (contactMethod === '訪問') {
      const addressEl = form.elements.namedItem('address') as HTMLInputElement;
      if (!addressEl || !addressEl.value.trim()) {
        addressEl?.focus();
        return;
      }
    }

    setStatus('submitting');

    const formData = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name) {
        if (el instanceof HTMLInputElement && el.type === 'checkbox') {
          if (el.checked) formData.append(el.name, el.value);
        } else if (el instanceof HTMLInputElement && el.type === 'radio') {
          if (el.checked) formData.append(el.name, el.value);
        } else {
          formData.append(el.name, el.value);
        }
      }
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d75i4ar41deorl02fcug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
        setContactMethod('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-navy overflow-hidden">
      <div
        ref={ref}
        className="max-w-7xl mx-auto px-4 md:px-8"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        <div className="text-center mb-12">
          <span className="inline-block text-accent font-display font-bold text-xs tracking-widest uppercase mb-3">
            Contact
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-3">
            今すぐ、あなたのトイレ問題を<br />
            <span className="text-accent">48時間以内</span>に解決しませんか？
          </h2>
          <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            LINEで写真を送るだけ。その場で正確見積もり。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10 items-start">
          <div className="lg:col-span-2 flex flex-col gap-5">
            {/* LINE バナー */}
            <a
              href="https://lin.ee/Su7CYDD"
              rel="nofollow"
              target="_blank"
              className="flex items-center gap-4 bg-[#00B900] hover:bg-[#009900] text-white rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-line-fill text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-black text-lg leading-tight">LINEで無料見積もり</p>
                <p className="text-white/80 text-sm">写真を送るだけ！最短30分で返信</p>
              </div>
              <span className="w-5 h-5 flex items-center justify-center ml-auto">
                <i className="ri-arrow-right-line text-white text-lg"></i>
              </span>
            </a>

            {/* 電話バナー */}
            <a
              href="tel:0297386660"
              className="flex items-center gap-4 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-accent/20 rounded-xl flex-shrink-0">
                <i className="ri-phone-fill text-accent text-2xl"></i>
              </div>
              <div>
                <p className="text-white/70 text-xs mb-0.5">お電話でのご相談</p>
                <p className="font-display font-black text-white text-xl leading-tight">0297-38-6660</p>
                <p className="text-white/50 text-xs mt-0.5">9:00〜18:00（定休：日曜・木曜）</p>
              </div>
              <span className="w-5 h-5 flex items-center justify-center ml-auto">
                <i className="ri-arrow-right-line text-white/60 text-lg"></i>
              </span>
            </a>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="font-bold text-white text-sm mb-3">よくある質問</h3>
              <ul className="space-y-3">
                {[
                  { q: '見積もりは無料ですか？', a: 'はい、完全無料です。' },
                  { q: '賃貸物件でも依頼できますか？', a: '管理会社への確認が必要ですが対応可能です。' },
                  { q: '古いトイレでも対応できますか？', a: 'どんな古い機種でもお気軽にご相談ください。' },
                ].map((item) => (
                  <li key={item.q} className="text-xs">
                    <p className="text-white font-medium mb-0.5">Q. {item.q}</p>
                    <p className="text-white/60">A. {item.a}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-3">
            {status === 'success' ? (
              <div className="bg-white/10 border border-white/20 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-accent/20 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-accent text-3xl"></i>
                </div>
                <h3 className="font-black text-white text-xl mb-2">送信完了しました！</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  担当者より48時間以内にご連絡いたします。
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-accent text-sm hover:underline cursor-pointer"
                >
                  別のお問い合わせをする
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-6 md:p-8"
              >
                <h3 className="font-black text-navy text-xl mb-6">お問い合わせフォーム</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-navy text-xs font-bold mb-1.5" htmlFor="name">
                      お名前 <span className="text-accent">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:border-accent transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-navy text-xs font-bold mb-1.5" htmlFor="email">
                      メールアドレス <span className="text-accent">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="example@email.com"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:border-accent transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-navy text-xs font-bold mb-1.5" htmlFor="phone">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="090-1234-5678"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:border-accent transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-navy text-xs font-bold mb-1.5" htmlFor="area">
                      お住まいのエリア <span className="text-accent">*</span>
                    </label>
                    <select
                      id="area"
                      name="area"
                      required
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:border-accent transition-colors bg-white"
                    >
                      <option value="">選択してください</option>
                      <option value="つくばみらい市">つくばみらい市</option>
                      <option value="守谷市">守谷市</option>
                      <option value="取手市">取手市</option>
                      <option value="龍ヶ崎市">龍ヶ崎市</option>
                      <option value="牛久市">牛久市</option>
                      <option value="利根町">利根町</option>
                      <option value="つくば市">つくば市</option>
                      <option value="阿見町">阿見町</option>
                      <option value="常総市">常総市</option>
                      <option value="我孫子市">我孫子市</option>
                      <option value="その他">その他（要確認）</option>
                    </select>
                  </div>
                </div>

                {/* 希望のご連絡方法 */}
                <div className="mb-4">
                  <label className="block text-navy text-xs font-bold mb-2">
                    希望のご連絡方法 <span className="text-accent">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: 'メール', icon: 'ri-mail-line', label: 'メール' },
                      { value: '電話', icon: 'ri-phone-line', label: '電話' },
                      { value: '訪問', icon: 'ri-map-pin-line', label: '訪問（現地確認）' },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium cursor-pointer transition-all duration-200 ${
                          contactMethod === opt.value
                            ? 'border-accent bg-accent/5 text-accent'
                            : 'border-gray-200 text-navy hover:border-accent/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="contact_method"
                          value={opt.value}
                          required
                          className="sr-only"
                          checked={contactMethod === opt.value}
                          onChange={() => setContactMethod(opt.value)}
                        />
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className={`${opt.icon} text-sm`}></i>
                        </span>
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 希望のご連絡時間（電話選択時のみ表示） */}
                {contactMethod === '電話' && (
                  <div className="mb-4 bg-orange-50 border border-orange-100 rounded-lg px-4 py-3">
                    <label className="block text-navy text-xs font-bold mb-1.5" htmlFor="preferred_time">
                      <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                        <i className="ri-time-line text-accent text-xs"></i>
                      </span>
                      希望のご連絡時間 <span className="text-accent">*</span>
                      <span className="text-gray-400 font-normal ml-1">（営業時間内）</span>
                    </label>
                    <select
                      id="preferred_time"
                      name="preferred_time"
                      required={contactMethod === '電話'}
                      className="w-full border border-orange-200 rounded-lg px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:border-accent transition-colors bg-white"
                    >
                      <option value="">ご希望のお時間帯を選択</option>
                      <option value="9:00〜11:00">9:00〜11:00</option>
                      <option value="11:00〜13:00">11:00〜13:00</option>
                      <option value="13:00〜15:00">13:00〜15:00</option>
                      <option value="15:00〜17:00">15:00〜17:00</option>
                      <option value="17:00〜18:00">17:00〜18:00</option>
                    </select>
                  </div>
                )}

                {/* ご住所（任意 / 訪問時は必須） */}
                <div className="mb-4">
                  <label className="block text-navy text-xs font-bold mb-1.5" htmlFor="address">
                    ご住所
                    {contactMethod === '訪問' ? (
                      <>
                        <span className="text-accent ml-1">*</span>
                        <span className="text-accent text-xs font-normal ml-1">（現地確認のため必須）</span>
                      </>
                    ) : (
                      <span className="text-gray-400 font-normal ml-1">（任意）</span>
                    )}
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    required={contactMethod === '訪問'}
                    placeholder="茨城県つくばみらい市○○ 123-4"
                    className={`w-full border rounded-lg px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:border-accent transition-colors ${
                      contactMethod === '訪問'
                        ? 'border-accent/50 bg-orange-50/30'
                        : 'border-gray-200'
                    }`}
                  />
                  {contactMethod === '訪問' && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <span className="w-3 h-3 inline-flex items-center justify-center">
                        <i className="ri-information-line text-xs text-accent"></i>
                      </span>
                      現地確認に伺うため、正確な住所をご入力ください。
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-navy text-xs font-bold mb-2">
                    ご希望のプラン <span className="text-gray-400 font-normal">（複数選択可）</span>
                  </label>
                  <div className="border border-gray-200 rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto bg-gray-50/50">
                    {[
                      { value: '未定（相談したい）', label: '未定（相談したい）', sub: '' },
                      { value: 'PLAN1 スタンダード｜LIXIL アメージュ+KB｜¥150,000（税抜）', label: 'PLAN1 スタンダード', sub: 'LIXIL アメージュ+シャワートイレKB｜¥150,000（税抜）' },
                      { value: 'PLAN2 アドバンス｜LIXIL アメージュ+KA｜¥170,000（税抜）', label: 'PLAN2 アドバンス', sub: 'LIXIL アメージュ+シャワートイレKA｜¥170,000（税抜）' },
                      { value: 'PLAN3 プレミアム｜LIXIL アメージュ+New PASSO｜¥200,000（税抜）', label: 'PLAN3 プレミアム', sub: 'LIXIL アメージュ+シャワートイレNew PASSO｜¥200,000（税抜）' },
                      { value: 'PLAN4 スタンダード｜TOTO ピュアレストQR+ウォシュレットSS1｜¥160,000（税抜）', label: 'PLAN4 スタンダード', sub: 'TOTO ピュアレストQR+ウォシュレットSS1｜¥160,000（税抜）' },
                      { value: 'PLAN5 アドバンス｜TOTO ピュアレストQR+ウォシュレットSS2A｜¥180,000（税抜）', label: 'PLAN5 アドバンス', sub: 'TOTO ピュアレストQR+ウォシュレットSS2A｜¥180,000（税抜）' },
                      { value: 'PLAN6 プレミアム｜TOTO ピュアレストQR+アプリコットF3A｜¥210,000（税抜）', label: 'PLAN6 プレミアム', sub: 'TOTO ピュアレストQR+アプリコットF3A｜¥210,000（税抜）' },
                      { value: 'PLAN7 オプション｜内装パック工事｜¥50,000（税抜）', label: 'PLAN7 オプション', sub: '内装パック工事｜¥50,000（税抜）' },
                      { value: 'PLAN8 オプション｜床補強+内装パック工事｜¥80,000（税抜）', label: 'PLAN8 オプション', sub: '床補強+内装パック工事｜¥80,000（税抜）' },
                      { value: 'PLAN9 オプション｜アクセサリー交換パック｜¥10,000（税抜）', label: 'PLAN9 オプション', sub: 'アクセサリー交換パック｜¥10,000（税抜）' },
                      { value: 'PLAN10 オプション｜電気パック工事｜¥20,000（税抜）', label: 'PLAN10 オプション', sub: '電気パック工事｜¥20,000（税抜）' },
                      { value: 'PLAN11 オプション｜換気扇交換工事｜¥20,000（税抜）', label: 'PLAN11 オプション', sub: '換気扇交換工事｜¥20,000（税抜）' },
                      { value: 'PLAN12 オプション｜安心バリアフリーパック工事｜¥70,000（税抜）', label: 'PLAN12 オプション', sub: '安心バリアフリーパック工事｜¥70,000（税抜）' },
                    ].map((item) => (
                      <label key={item.value} className="flex items-start gap-2.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          name="plan"
                          value={item.value}
                          className="mt-0.5 w-4 h-4 accent-accent flex-shrink-0 cursor-pointer"
                        />
                        <span className="flex flex-col">
                          <span className="text-xs font-bold text-navy group-hover:text-accent transition-colors">{item.label}</span>
                          {item.sub && <span className="text-xs text-gray-500 leading-tight">{item.sub}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-navy text-xs font-bold mb-1.5" htmlFor="message">
                    ご相談内容・お問い合わせ <span className="text-accent">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="例：築15年のトイレを交換したい。できるだけ早く対応してほしいです。"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-navy focus:outline-none focus:border-accent transition-colors resize-none"
                  />
                  <p className={`text-xs text-right mt-1 ${charCount > 480 ? 'text-accent' : 'text-gray-400'}`}>
                    {charCount}/500文字
                  </p>
                </div>

                {status === 'error' && (
                  <div className="mb-4 flex items-center gap-2 text-red-500 text-sm bg-red-50 rounded-lg px-3 py-2">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-error-warning-line text-sm"></i>
                    </span>
                    送信に失敗しました。時間をおいて再度お試しください。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting' || charCount > 500}
                  className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent-dark disabled:bg-gray-300 text-white py-4 rounded-full font-bold text-base whitespace-nowrap transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? (
                    <>
                      <span className="w-5 h-5 flex items-center justify-center animate-spin">
                        <i className="ri-loader-4-line text-lg"></i>
                      </span>
                      送信中...
                    </>
                  ) : (
                    <>
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-send-plane-fill text-lg"></i>
                      </span>
                      無料相談を申し込む
                    </>
                  )}
                </button>

                <p className="text-gray-400 text-xs text-center mt-3">
                  ご入力いただいた情報は、お問い合わせ対応のみに使用します。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
